import { useEffect, useState } from "react";
import { CloudSun, Moon, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { greetingFor } from "@/components/Greeting";

let playedFor = null;
export const resetGreetingIntro = () => { playedFor = null; };

const ICONS = { "Good morning": [Sun, "text-amber-500"], "Good afternoon": [CloudSun, "text-orange-500"], "Good evening": [Moon, "text-indigo-600"] };
const TZ = "Asia/Muscat";
const CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
const HOUR = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "numeric", hourCycle: "h23" });

const muscat = (d) => {
  const p = Object.fromEntries(CLOCK.formatToParts(d).map((x) => [x.type, x.value]));
  return { date: `${p.day}/${p.month}/${p.year}`, hh: p.hour === "00" ? "12" : p.hour, mm: p.minute, ap: (p.dayPeriod || "").toUpperCase(), hour: Number(HOUR.format(d)) };
};

/** Muscat clock that ticks on each minute boundary. */
const useMuscatClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let t;
    const schedule = () => { t = setTimeout(() => { setNow(new Date()); schedule(); }, 60000 - (Date.now() % 60000) + 50); };
    const wake = () => { if (document.visibilityState === "visible") { clearTimeout(t); setNow(new Date()); schedule(); } };
    schedule();
    document.addEventListener("visibilitychange", wake);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", wake); };
  }, []);
  return muscat(now);
};

/** Header greeting (Muscat time of day) with the live Muscat date and time underneath; intro plays once per sign-in, after the splash. */
export const HeaderGreeting = () => {
  const { user } = useAuth();
  const c = useMuscatClock();
  const [phase, setPhase] = useState(() => (playedFor === user?.id ? "done" : "wait"));
  useEffect(() => {
    if (phase !== "wait") return undefined;
    const go = () => { playedFor = user?.id; setPhase("play"); };
    if (!document.querySelector('[data-testid="splash"]')) { go(); return undefined; }
    window.addEventListener("splash:done", go, { once: true });
    return () => window.removeEventListener("splash:done", go);
  }, [phase, user?.id]);
  const word = greetingFor(c.hour);
  const [Icon, tone] = ICONS[word];
  const first = (user?.name || user?.email?.split("@")[0] || "").trim().split(/\s+/)[0];
  const cls = phase === "play" ? "greet-play" : phase === "wait" ? "opacity-0" : "";
  return (
    <span className={`flex max-w-full flex-col items-start ${cls}`} data-testid="header-greeting">
      <span className="greet-line flex min-w-0 max-w-full items-center gap-1 text-[15px] font-semibold leading-5 text-slate-800 lg:text-[17px] lg:leading-[1.3]">
        <Icon className={`greet-icon h-4 w-4 shrink-0 lg:h-[18px] lg:w-[18px] ${tone}`} strokeWidth={2.4} aria-hidden="true" data-testid="header-greeting-icon" />
        <span className="truncate">{word}, <span className="greet-name" data-testid="header-greeting-name"><span className="sm:hidden">{first}</span><span className="hidden sm:inline">{user?.name || first}</span></span></span>
      </span>
      <span className="greet-date whitespace-nowrap text-[11px] font-medium leading-4 tabular-nums text-slate-500 lg:text-xs" data-testid="header-datetime" aria-label={`${c.date}, ${c.hh}:${c.mm} ${c.ap} Muscat time`}>
        {c.date} <span className="text-slate-300">|</span> {c.hh}<span className="clock-colon">:</span>{c.mm} {c.ap}
      </span>
    </span>
  );
};
